/**
 * randomUUID — a secure-context-safe UUID v4 generator.
 *
 * `crypto.randomUUID()` (and the whole `crypto.subtle` surface) is only exposed
 * in a *secure context*: HTTPS pages or `http://localhost`. On a plain-HTTP
 * origin served from an IP/host (e.g. a LoadBalancer without TLS) it is
 * `undefined`, so calling it throws "crypto.randomUUID is not a function".
 *
 * `crypto.getRandomValues()` is NOT gated to secure contexts, so we fall back to
 * it (and, last resort, Math.random) to build a v4 UUID that works everywhere.
 */
export function randomUUID(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // RFC 4122 v4: set version (4) and variant (10xx) bits.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return `${h[0]}${h[1]}${h[2]}${h[3]}-${h[4]}${h[5]}-${h[6]}${h[7]}-${h[8]}${h[9]}-${h[10]}${h[11]}${h[12]}${h[13]}${h[14]}${h[15]}`;
}
